package diana.soleil.design.iraniancanadienbazzar.feature.addbusiness

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppBottomSheet
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppButtonVariant
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppChip
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppIcon
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppTextField
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import diana.soleil.design.iraniancanadienbazzar.core.model.BusinessAttributeRegistry
import diana.soleil.design.iraniancanadienbazzar.core.model.Category
import diana.soleil.design.iraniancanadienbazzar.core.model.Language
import diana.soleil.design.iraniancanadienbazzar.core.model.LocalizedText
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.Res
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_cancel
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_info_category_label
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_info_category_placeholder
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_info_desc_counter
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_info_desc_helper
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_info_desc_label
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_info_details_label
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_info_desc_placeholder
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_info_name_helper
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_info_name_label
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_info_name_placeholder
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_info_promo
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_info_tile1_desc
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_info_tile1_title
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_info_tile2_desc
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_info_tile2_title
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_lang_autotranslate_hint
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_lang_will_translate
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_error_category
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_error_required
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_info_title
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.resources.ab_next
import org.jetbrains.compose.resources.stringResource

private const val DESCRIPTION_MAX = 250

/** Step 1 — Basic Business Info (Stitch `8c2035f6`). Name + description are per-language (EN/FR/FA). */
@Composable
@OptIn(ExperimentalLayoutApi::class)
fun AddBusinessInfoScreen(
    draft: AddBusinessDraft,
    categories: List<Category>,
    onName: (Language, String) -> Unit,
    onCategory: (String) -> Unit,
    onToggleAttribute: (String) -> Unit,
    onDescription: (Language, String) -> Unit,
    onBack: () -> Unit,
    onCancel: () -> Unit,
    onNext: () -> Unit,
    modifier: Modifier = Modifier,
) {
    // Inline validation (UX-9): Next stays tappable; an invalid tap lights up WHICH fields are
    // missing instead of a silently disabled button the owner has to puzzle over.
    var showErrors by remember { mutableStateOf(false) }
    WizardScaffold(
        stepIndex = 1,
        title = stringResource(Res.string.ab_info_title),
        onBack = onBack,
        modifier = modifier,
        bottomBar = {
            Row(horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.md)) {
                AppButton(
                    text = stringResource(Res.string.ab_cancel),
                    onClick = onCancel,
                    modifier = Modifier.weight(1f),
                    variant = AppButtonVariant.Secondary,
                )
                AppButton(
                    text = stringResource(Res.string.ab_next),
                    onClick = { if (draft.isInfoValid) onNext() else showErrors = true },
                    modifier = Modifier.weight(1f),
                    trailingIcon = "arrow_forward",
                )
            }
        },
    ) {
        // Auto-translate explainer: write in one language, we fill the rest (owner direction).
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .background(AppTheme.colors.secondaryContainer, AppTheme.shapes.card)
                .padding(AppTheme.spacing.lg),
            horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            AppIcon("translate", tint = AppTheme.colors.onSecondaryContainer)
            Text(
                text = stringResource(Res.string.ab_lang_autotranslate_hint),
                style = AppTheme.typography.bodySm,
                color = AppTheme.colors.onSecondaryContainer,
            )
        }

        FormCard {
            LocalizedInput(
                value = draft.name,
                onValueChange = onName,
                label = stringResource(Res.string.ab_info_name_label),
                placeholder = stringResource(Res.string.ab_info_name_placeholder),
                helper = stringResource(Res.string.ab_info_name_helper),
                isError = showErrors && draft.name.isEmpty,
                errorText = stringResource(Res.string.ab_error_required),
            )

            CategoryField(
                selected = categories.firstOrNull { it.id == draft.categoryId },
                categories = categories,
                onSelect = onCategory,
            )
            if (showErrors && draft.categoryId == null) {
                Text(
                    text = stringResource(Res.string.ab_error_category),
                    style = AppTheme.typography.bodySm,
                    color = AppTheme.colors.error,
                )
            }

            // Structured facts, unlocked by the picked category (Google-Business-Profile pattern —
            // see BusinessAttributeRegistry). Optional; labels are localized content, not chrome.
            if (draft.categoryId != null) {
                Text(
                    text = stringResource(Res.string.ab_info_details_label),
                    style = AppTheme.typography.labelMd,
                    color = AppTheme.colors.onSurface,
                )
                FlowRow(
                    horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
                    verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
                ) {
                    BusinessAttributeRegistry.forCategory(draft.categoryId).forEach { def ->
                        AppChip(
                            label = def.label.resolve(AppTheme.language),
                            selected = def.key in draft.attributes,
                            onClick = { onToggleAttribute(def.key) },
                        )
                    }
                }
            }

            LocalizedInput(
                value = draft.description,
                onValueChange = onDescription,
                label = stringResource(Res.string.ab_info_desc_label),
                placeholder = stringResource(Res.string.ab_info_desc_placeholder),
                helper = stringResource(Res.string.ab_info_desc_helper),
                singleLine = false,
                maxChars = DESCRIPTION_MAX,
                isError = showErrors && draft.description.isEmpty,
                errorText = stringResource(Res.string.ab_error_required),
            )
        }

        InfoTile("verified", stringResource(Res.string.ab_info_tile1_title), stringResource(Res.string.ab_info_tile1_desc))
        InfoTile("visibility", stringResource(Res.string.ab_info_tile2_title), stringResource(Res.string.ab_info_tile2_desc))

        // Promo quote
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .background(AppTheme.colors.primaryContainer, AppTheme.shapes.card)
                .padding(AppTheme.spacing.lg),
        ) {
            Text(
                text = stringResource(Res.string.ab_info_promo),
                style = AppTheme.typography.bodyMd,
                color = AppTheme.colors.onPrimary,
            )
        }
    }
}

@Composable
private fun FormCard(content: @Composable ColumnScope.() -> Unit) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .background(AppTheme.colors.surface, AppTheme.shapes.card)
            .padding(AppTheme.spacing.lg),
        verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.lg),
        content = content,
    )
}

/** A single field authored in EN/FR/FA via a language tab row. Stores per-language into [LocalizedText]. */
@Composable
private fun LocalizedInput(
    value: LocalizedText,
    onValueChange: (Language, String) -> Unit,
    label: String,
    placeholder: String,
    helper: String? = null,
    singleLine: Boolean = true,
    maxChars: Int? = null,
    isError: Boolean = false,
    errorText: String? = null,
) {
    val defaultLang = AppTheme.language
    var lang by remember { mutableStateOf(defaultLang) }
    val current = value[lang].orEmpty()

    Column(verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm)) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text(text = label, style = AppTheme.typography.labelMd, color = AppTheme.colors.onSurfaceVariant)
            if (maxChars != null) {
                Text(
                    text = stringResource(Res.string.ab_info_desc_counter, current.length, maxChars),
                    style = AppTheme.typography.labelSm,
                    color = AppTheme.colors.onSurfaceVariant,
                )
            }
        }
        Row(horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm)) {
            Language.entries.forEach { option ->
                // A filled language gets a ✓ so owners can see at a glance what they've entered
                // and which languages will be auto-translated.
                val filled = value[option].orEmpty().isNotBlank()
                AppChip(
                    label = if (filled) "${option.endonym} ✓" else option.endonym,
                    selected = option == lang,
                    onClick = { lang = option },
                )
            }
        }
        AppTextField(
            value = current,
            onValueChange = { onValueChange(lang, if (maxChars != null) it.take(maxChars) else it) },
            placeholder = placeholder,
            singleLine = singleLine,
            isError = isError,
            supportingText = errorText?.takeIf { isError },
            modifier = if (singleLine) Modifier else Modifier.heightIn(min = AppTheme.sizes.reviewCommentMinHeight),
        )
        // "We'll translate the rest" note — shown once at least one language is filled but not all.
        val filledCount = Language.entries.count { value[it].orEmpty().isNotBlank() }
        if (filledCount in 1 until Language.entries.size) {
            Text(
                text = stringResource(Res.string.ab_lang_will_translate, filledCount, Language.entries.size),
                style = AppTheme.typography.bodySm,
                color = AppTheme.colors.primary,
            )
        } else if (helper != null) {
            Text(text = helper, style = AppTheme.typography.bodySm, color = AppTheme.colors.onSurfaceVariant)
        }
    }
}

@Composable
private fun CategoryField(selected: Category?, categories: List<Category>, onSelect: (String) -> Unit) {
    var showSheet by remember { mutableStateOf(false) }

    Column(verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm)) {
        Text(
            text = stringResource(Res.string.ab_info_category_label),
            style = AppTheme.typography.labelMd,
            color = AppTheme.colors.onSurfaceVariant,
        )
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .background(AppTheme.colors.surfaceContainer, AppTheme.shapes.md)
                .clickable { showSheet = true }
                .padding(AppTheme.spacing.md),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text(
                text = selected?.displayName(AppTheme.language) ?: stringResource(Res.string.ab_info_category_placeholder),
                style = AppTheme.typography.bodyMd,
                color = if (selected != null) AppTheme.colors.onSurface else AppTheme.colors.onSurfaceVariant,
            )
            AppIcon("expand_more", tint = AppTheme.colors.onSurfaceVariant)
        }
    }

    if (showSheet) {
        AppBottomSheet(
            onDismissRequest = { showSheet = false },
            title = stringResource(Res.string.ab_info_category_label),
        ) {
            categories.forEach { category ->
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .clickable { onSelect(category.id); showSheet = false }
                        .padding(vertical = AppTheme.spacing.md),
                    horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    AppIcon(name = category.iconKey, tint = AppTheme.colors.primary)
                    Text(
                        text = category.displayName(AppTheme.language),
                        style = AppTheme.typography.bodyLg,
                        color = AppTheme.colors.onSurface,
                        modifier = Modifier.weight(1f),
                    )
                    if (category.id == selected?.id) {
                        AppIcon("check", tint = AppTheme.colors.primary)
                    }
                }
            }
        }
    }
}

@Composable
private fun InfoTile(icon: String, title: String, description: String) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(AppTheme.colors.secondaryContainer, AppTheme.shapes.card)
            .padding(AppTheme.spacing.lg),
        horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
    ) {
        AppIcon(name = icon, tint = AppTheme.colors.onSecondaryContainer)
        Column(verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.xxs)) {
            Text(text = title, style = AppTheme.typography.labelMd, color = AppTheme.colors.onSecondaryContainer)
            Text(text = description, style = AppTheme.typography.bodySm, color = AppTheme.colors.onSecondaryContainer)
        }
    }
}
